import { STORY_SOURCE, story } from '@/content/storyBuilder'

// Written from the five-part full-page capture of digitopia.co/customers/temsa/
// supplied on 2026-08-29 — "tıpatıp tastamam" — together with six photographs.
// Four of them are placed: the Avenue electron in the light tunnel is the
// header picture the reference runs there, the TS45 E is the wide picture
// between the standfirst and the story, and the Adana plant and the Avenue
// electron in the city fill the two panel halves the capture leaves blank.
// The closing quote is not a `section`: Erdinç Uğurlu's words are already in
// `content/testimonials.ts` keyed to this slug, with TEMSA's mark and his
// portrait, and `StoryBody` draws that card.
export const added = story('temsa', 'TEMSA', {
  doc: 'ed5016da-0657-40e2-9164-f61730f6c90f-Temsa and Digitopia.pdf',
  heroPhoto: {
    src: '/photo/temsa-avenue-electron-tunnel.webp',
    alt: 'A TEMSA Avenue electron city bus standing under a sweeping arc of blue light.',
  },
  lede: [
    'TEMSA utilized the Digital Maturity Index (DMI) and the Impact Platform to both assess '
    + 'and speed up its journey to becoming a digitally advanced manufacturer.',
  ],
  intro:
    'As a leading player in transportation and mobility solutions, TEMSA is known for its '
    + 'cutting-edge vehicles and strong R&D capabilities. Yet in today’s world, engineering '
    + 'excellence alone isn’t enough. Sustained leadership requires digital clarity, strategic '
    + 'alignment, and measurable progress. That’s why TEMSA partnered with Digitopia for a '
    + 'full Digital Maturity Index (DMI) assessment, not once but twice.',
  photo: {
    src: '/photo/temsa-ts45e-electric-coach.webp',
    alt: 'A TEMSA TS45 E all-electric coach in “the new vision of energy” livery on a city '
      + 'road.',
  },
  sections: [
    {
      heading: 'The Challenge: Scaling Smart Manufacturing Investments',
      centred: true,
      body: [
        'Like many industrial leaders, TEMSA recognized that digital transformation should not '
        + 'be left to guesswork. While various initiatives were already underway, from smart '
        + 'manufacturing investments to digital customer services, the company lacked a unified '
        + 'framework to evaluate progress or compare its standing within the broader industry '
        + 'and holding ecosystem.',
        'The need was clear:',
        'Establish a baseline to understand where they truly stood,',
        'Track progress over time using measurable insights,',
        'Benchmark performance against peers and other holdings,',
        'Align investments with strategic outcomes.',
      ],
    },
    {
      heading: 'The Solution: Clarity, Benchmarking, and Strategic Tracking with DMI',
      band: true,
      photo: {
        src: '/photo/temsa-adana-plant-aerial.webp',
        alt: 'An aerial view of TEMSA’s Adana plant, with finished buses lined up beside the '
          + 'production halls.',
      },
      photoSide: 'right',
      photoInPanel: true,
      body: [
        'Digitopia’s DMI approach gave TEMSA the tools and structure to transform vision into '
        + 'data-driven execution.',
      ],
      items: [
        {
          lead: 'Repeatable and Reliable Measurement',
          text: 'Over two assessment cycles, TEMSA tracked its digital progress using the same '
            + 'structured framework, which enabled clear before-and-after comparisons and '
            + 'identified tangible improvement areas.',
        },
        {
          lead: 'Objective Insight for Smart Investment',
          text: 'With the DMI, TEMSA no longer relies on assumptions. The company now measures '
            + 'its digital progress with precision, ensuring that each investment, whether in '
            + 'automation, AI, or customer solutions, is grounded in real business value.',
        },
        {
          lead: 'Benchmarking Across Holding',
          text: 'TEMSA appreciated the ability to benchmark not only against industry peers but '
            + 'also against other companies within its own holding group. This feature enabled '
            + 'strategic discussions at the group level, facilitating better alignment across '
            + 'portfolios.',
        },
        {
          lead: 'A Data-Driven Transformation Journey',
          text: 'The DMI has become more than a measurement tool for TEMSA; it is a compass. It '
            + 'ensures that the journey is not only visionary but also trackable, comparable, '
            + 'and impactful.',
        },
      ],
    },
    {
      heading: 'Benefits: A Data-Driven Transformation Roadmap',
      band: true,
      photo: {
        src: '/photo/temsa-avenue-electron-city.webp',
        alt: 'A TEMSA Avenue electron electric bus on a city avenue at sunset, its destination '
          + 'board reading TEMSA.',
      },
      photoSide: 'left',
      items: [
        {
          lead: 'Clear Baseline, Measurable Progress',
          text: 'TEMSA now operates with a structured, repeatable framework to evaluate digital '
            + 'capabilities, transforming assumptions into actionable insights. The two-cycle '
            + 'assessment made it possible to measure tangible progress and ensure consistent '
            + 'improvement.',
        },
        {
          lead: 'Smarter Investment Decisions',
          text: 'With data-driven clarity, TEMSA can now prioritize digital initiatives that '
            + 'generate the highest impact. The DMI helps ensure resources are allocated '
            + 'strategically, where they matter most.',
        },
        {
          lead: 'Group-Level Benchmarking',
          text: 'Thanks to cross-holding comparisons, TEMSA gains a broader perspective on '
            + 'performance. This visibility strengthens internal positioning and encourages '
            + 'constructive dialogue with other group companies.',
        },
        {
          lead: 'Alignment Across Leadership',
          text: 'The DMI fostered alignment between IT, operations, and business units. A '
            + 'common language around digital maturity helped accelerate decision-making and '
            + 'build shared accountability across teams.',
        },
        {
          lead: 'Transformation with Confidence',
          text: 'Digitopia’s methodology empowers TEMSA to move forward with confidence, making '
            + 'digital transformation not just a vision, but a trackable and guided journey. '
            + 'With each assessment, the company sharpens its roadmap and renews its focus.',
        },
      ],
    },
    {
      heading: 'Conclusion: A Data-Driven Approach to Transformation',
      band: true,
      centred: true,
      body: [
        'Today, TEMSA approaches transformation with greater confidence and clarity. Leadership '
        + 'is aligned, priorities are sharper, and progress is visible. With each DMI '
        + 'assessment, TEMSA strengthens its ability to evaluate impact, refine its roadmap, '
        + 'and make smarter investments, ensuring that transformation efforts are both targeted '
        + 'and sustainable. Through its continued partnership with Digitopia, TEMSA is not only '
        + 'shaping the future of mobility; it is measuring it every step of the way.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/temsa/ supplied on 2026-08-29, not from a file we hold, so please `
    + 'proofread it against the source before production. Three deliberate differences from '
    + 'the reference, each of them a template-wide decision rather than this page’s: the '
    + 'reference’s “SUCCESS STORIES” eyebrow above the headline is gone, because customer '
    + 'pages here no longer carry one; the reference repeats a “Read More” button inside both '
    + 'the Solution and the Benefits panels, which here is the single “Read the Story” button '
    + 'in the header, pointing at the PDF; and the closing quote is the Hall of Fame card, so '
    + 'its eyebrow is the company where the reference sets Erdinç Uğurlu’s name — his name and '
    + 'title are on the line under the quote either way. Two things to look at rather than '
    + 'decide. The right half of the reference’s Solution panel and the left half of its '
    + 'Benefits panel are both blank in the capture, and two of the photographs supplied with '
    + 'it that the capture never shows — the Adana plant from the air and the Avenue electron '
    + 'in the city — are placed in them, on the assumption that they are what did not load; '
    + 'move or drop either if that is wrong. A sixth photograph, a second aerial of the plant '
    + 'at sunset, is not used and is not in the repository. And the four lines under “The need '
    + 'was clear:” are set as centred paragraphs, matching the reference’s own unmarked centred '
    + 'lines rather than this template’s bulleted list, so they sit slightly further apart than '
    + 'the capture does. One production note: the header photograph’s original is 768px wide, '
    + 'which is what the live site serves, so it will look soft on a high-density screen until '
    + 'a larger file arrives; the other three are 1200–1600px.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 4
